/**
 * **Demo-Termine** — Themenkalender für die Buchung auf der Website (nur Plattform-Root).
 *
 * Je Thema (z. B. „betreiberpflichten“ für /termin/betreiberpflichten/) eine Liste von Hosts. Was ein Host in
 * seinen Outlook-Kalender als „Demo-Slot“ stellt, ist auf der Website buchbar. Hier: Themen anlegen, Hosts
 * zuordnen, die freien Slots mit Host ansehen. tri-server docs/demo-booking.md.
 */
import { useEffect, useState } from "react";
import { CalendarClock, Plus, Save, Trash2 } from "lucide-react";
import { useModalContext } from "../components/ModalProvider";
import { DemoSlot, DemoTopic, useDemoBookingAPI } from "../DemoBookingAPI";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";

const SITE = "https://www.the-real-insight.ai";
const EMPTY: DemoTopic = { key: "", title: "", description: "", hosts: [], active: true, minNoticeHours: 12, horizonDays: 42, slotMinutes: 30 };

const formatSlot = (slot: DemoSlot) =>
  `${new Date(slot.start).toLocaleString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" })}–${new Date(slot.end).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" })}`;

export default function DemoTopicsPanel() {
  const api = useDemoBookingAPI();
  const { pushError } = useModalContext() as any;
  const [topics, setTopics] = useState<DemoTopic[] | null>(null);
  const [draft, setDraft] = useState<DemoTopic | null>(null);
  const [hostsText, setHostsText] = useState("");
  const [isNew, setIsNew] = useState(false);
  const [slots, setSlots] = useState<DemoSlot[] | null>(null);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const load = () =>
    api
      .listTopics()
      .then(setTopics)
      .catch((error: any) => pushError(error));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const edit = (topic: DemoTopic, fresh = false) => {
    setDraft({ ...topic });
    setHostsText((topic.hosts ?? []).join("\n"));
    setIsNew(fresh);
    setSlots(null);
  };

  const save = async () => {
    if (!draft) return;
    try {
      const saved = await api.saveTopic({ ...draft, hosts: hostsText.split(/[\s,;]+/).filter(Boolean) });
      toast.success(`Thema „${saved.title}“ gespeichert`);
      edit(saved);
      load();
    } catch (error: any) {
      pushError(error);
    }
  };

  const remove = async () => {
    if (!draft || isNew) return;
    try {
      await api.deleteTopic(draft.key);
      toast.success("Thema gelöscht");
      setDraft(null);
      load();
    } catch (error: any) {
      pushError(error);
    }
  };

  const showSlots = async () => {
    if (!draft || isNew) return;
    setLoadingSlots(true);
    try {
      setSlots(await api.slots(draft.key));
    } catch (error: any) {
      pushError(error);
    } finally {
      setLoadingSlots(false);
    }
  };

  return (
    <div className="flex min-w-0 flex-col gap-4 p-4" data-testid="demo-topics-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl text-sm text-muted-foreground">
          Je Thema eine Buchungsseite auf der Website. Was die Hosts in ihren Outlook-Kalender als „Demo-Slot“ stellen (Schreibweise egal), ist dort buchbar. Nach der Bestätigung durch den Interessenten heißt
          der Termin „Demo-Slot mit … – Thema“, und der Interessent ist eingeladen.
        </div>
        <Button onClick={() => edit(EMPTY, true)}>
          <Plus className="mr-1 h-4 w-4" /> Thema anlegen
        </Button>
      </div>

      {!topics ? (
        <Skeleton className="h-24 w-full" />
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Thema</TableHead>
                <TableHead>Buchungsseite</TableHead>
                <TableHead>Hosts</TableHead>
                <TableHead className="text-right">Länge</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {topics.map(topic => (
                <TableRow key={topic.key} className="cursor-pointer" onClick={() => edit(topic)} data-state={draft?.key === topic.key ? "selected" : undefined}>
                  <TableCell className="font-medium">{topic.title}</TableCell>
                  <TableCell className="break-all text-xs">
                    <a href={`${SITE}/termin/${topic.key}/`} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} className="underline">
                      /termin/{topic.key}/
                    </a>
                  </TableCell>
                  <TableCell className="text-xs">{topic.hosts.join(", ") || "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{topic.slotMinutes ?? 30} min</TableCell>
                  <TableCell>{topic.active ? <Badge>aktiv</Badge> : <Badge variant="secondary">aus</Badge>}</TableCell>
                </TableRow>
              ))}
              {!topics.length ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-sm text-muted-foreground">
                    Noch kein Thema.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </div>
      )}

      {draft ? (
        <div className="grid min-w-0 gap-3 rounded-md border p-4 md:grid-cols-2">
          <label className="flex flex-col gap-1 text-sm">
            Schlüssel (Adresse /termin/…/)
            <Input value={draft.key} disabled={!isNew} placeholder="betreiberpflichten" onChange={e => setDraft({ ...draft, key: e.target.value.toLowerCase() })} />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Titel
            <Input value={draft.title} placeholder="Betreiberpflichten" onChange={e => setDraft({ ...draft, title: e.target.value })} />
          </label>
          <label className="flex flex-col gap-1 text-sm md:col-span-2">
            Beschreibung (auf der Buchungsseite)
            <Textarea rows={2} value={draft.description ?? ""} onChange={e => setDraft({ ...draft, description: e.target.value })} />
          </label>
          <label className="flex flex-col gap-1 text-sm md:col-span-2">
            Hosts (Postfächer im O365-Tenant, eines je Zeile)
            <Textarea rows={3} value={hostsText} placeholder={"marc@the-real-insight.com"} onChange={e => setHostsText(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Vorlauf (Stunden)
            <Input type="number" min={0} value={draft.minNoticeHours ?? 12} onChange={e => setDraft({ ...draft, minNoticeHours: Number(e.target.value) })} />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Horizont (Tage)
            <Input type="number" min={1} value={draft.horizonDays ?? 42} onChange={e => setDraft({ ...draft, horizonDays: Number(e.target.value) })} />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Slot-Länge (Minuten) — längere Demo-Slots werden in mehrere Zeiten geteilt
            <Input type="number" min={15} max={240} step={15} value={draft.slotMinutes ?? 30} onChange={e => setDraft({ ...draft, slotMinutes: Number(e.target.value) })} />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={draft.active} onCheckedChange={(active: boolean) => setDraft({ ...draft, active })} /> buchbar
          </label>
          <div className="flex flex-wrap items-center justify-end gap-2 md:col-span-2">
            {!isNew ? (
              <>
                <Button variant="outline" onClick={showSlots} disabled={loadingSlots}>
                  <CalendarClock className="mr-1 h-4 w-4" /> Freie Slots
                </Button>
                <Button variant="outline" onClick={remove}>
                  <Trash2 className="mr-1 h-4 w-4" /> Löschen
                </Button>
              </>
            ) : null}
            <Button onClick={save}>
              <Save className="mr-1 h-4 w-4" /> Speichern
            </Button>
          </div>
          {loadingSlots ? <Skeleton className="h-16 w-full md:col-span-2" /> : null}
          {slots ? (
            <div className="overflow-x-auto md:col-span-2">
              {slots.length ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Slot</TableHead>
                      <TableHead>Host</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {slots.map(slot => (
                      <TableRow key={slot.id}>
                        <TableCell className="tabular-nums">{formatSlot(slot)}</TableCell>
                        <TableCell className="text-xs">{slot.host}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Keine freien Slots. Steht „Demo-Slot“ in den Kalendern der Hosts? Fehlt der App die Berechtigung Calendars.ReadWrite, bleibt die Liste ebenfalls leer.
                </p>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
